import React, { memo, forwardRef, useImperativeHandle, useState, useCallback, useRef } from "react";
import Modal from "@mui/material/Modal";
import PropTypes from "prop-types";
import { useFormik } from "formik";

import api from "services/api";
import serveRequestErrors from "common/serve-request-errors";
import AppButton from "components/app-button";
import AppInput from "components/app-input";
import hero from "assets/images/components/pages/login/hero.png";

export const AppOtpModal = (props, ref) => {
    const { onSubmit, email, isResetPassword } = props;
    const [visible, setVisible] = useState(false);
    const [otp, setOtp] = useState(["", "", "", "", "", ""]);
    const inputsRef = useRef([]);
    const formik = useFormik({
        initialValues: {
            otp: "",
        },
        onSubmit: () => {},
    });

    const onHandleShow = useCallback(() => {
        setVisible(true);
        setOtp(["", "", "", "", "", ""]);
        setTimeout(() => inputsRef.current[0]?.focus(), 100);
    }, []);

    const onHandleDismiss = useCallback(() => {
        setVisible(false);
    }, []);

    //prettier-ignore
    useImperativeHandle(ref, () => ({
        onHandleShow: onHandleShow,
        onHandleDismiss: onHandleDismiss,
    }));

    const handleChange = (index, value) => {
        if (!/^\d?$/.test(value)) return;

        if (formik.errors.otp) {
            formik.setFieldError("otp", "");
        }

        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);

        if (value && index < 5) {
            inputsRef.current[index + 1]?.focus();
        }

        if (newOtp.every(char => char !== "")) {
            onHandleVerifyOtp(newOtp.join(""));
        }
    };

    const handleKeyDown = (e, index) => {
        if (e.key === "Backspace" && !otp[index] && index > 0) {
            inputsRef.current[index - 1]?.focus();
        }
    };

    const handleSubmit = (otpValue) => {
        onSubmit?.(otpValue);
        setVisible(false);
    };

    const onHandleVerifyOtp = async (otpCode) => {
        try{
            if (isResetPassword) {
                await api.post.general.verifyResetPasswordOtpCode({
                    otpCode: otpCode,
                    email: email,
                });                
            } else {
                await api.post.general.verifyOtpCode({otpCode: otpCode});
            }
            handleSubmit(otpCode);

        } catch (error) {
            serveRequestErrors(error, (message) => {
                formik.setFieldError("otp", message);
            });
        } finally {
            formik.setSubmitting(false);
        }
    };

    const onHandleSendOtp = async () => {
        try{
            if (isResetPassword) {
                await api.post.general.sendResetPasswordOtp({ email: email });
            }else {
                await api.get.general.sendOtp();
            }            
        } catch (error) {
            serveRequestErrors(error, (message) => {
                formik.setFieldError("otp", message);
            });
        } finally {
            formik.setSubmitting(false);
        }
    };

    return (
        <Modal classes={{ root: "app-otp-modal" }} open={visible} aria-labelledby="otp-modal" aria-describedby="app-otp-modal">
            <div className="otp-modal">
                <img className="otp-modal__hero" src={hero} alt="hero" />

                <div className="otp-modal__header">
                    <p className="otp-modal__title">OTP Verification</p>
                    <p className="otp-modal__description">A one-time passcode has been sent to <br /> <b>{ email }</b> </p>

                    <div className="otp-modal__otp-boxes">
                        {otp.map((val, i) => (
                            <AppInput
                                key={i}
                                value={val}
                                maxLength={1}
                                ref={(el) => (inputsRef.current[i] = el)}
                                onChange={(e) => handleChange(i, e.target.value)}
                                onKeyDown={(e) => handleKeyDown(e, i)}
                                className="otp-modal__otp-input"
                                type="text"
                                autoComplete="off"
                                name={`otp-${i}`}
                            />
                        ))}
                    </div>

                    {formik.errors.otp && (
                        <p className="otp-modal__error-message">{formik.errors.otp}</p>
                    )}

                    <p className="otp-modal__resend">
                        Didn’t receive code?{"  "}

                        <AppButton type="button" label="Resend OTP" className="otp-modal__resend-link" onClick={onHandleSendOtp} />
                    </p>

                </div>
                <div className="otp-modal__button-container">
                    <AppButton type="button" label="Close" onClick={onHandleDismiss} />
                </div>                

            </div>
        </Modal>
    );
};

export default memo(forwardRef(AppOtpModal));

AppOtpModal.propTypes = {
    ref: PropTypes.object,
    onSubmit: PropTypes.func,
    email: PropTypes.string,
};
